import { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { useLocation } from "wouter";
import { useBusinessContext } from "@/providers/business-provider";
import { 
  User, 
  Bell, 
  Shield, 
  CreditCard, 
  Building,
  Mail,
  Phone,
  Calendar,
  Settings,
  CheckCircle,
  AlertCircle,
  Camera,
  Upload,
  Edit
} from "lucide-react";
import { format } from "date-fns";

interface UserProfile {
  id: number;
  email: string;
  username: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  profilePicture?: string;
  phoneNumber?: string;
  twoFactorEnabled?: boolean;
  createdAt: string;
  emailNotifications: {
    reportsSummary: boolean;
    taskReminders: boolean;
    subscriptionAlerts: boolean;
    marketingEmails: boolean;
  };
  emailSchedule: {
    reportType: string;
    reportDay: string;
    active: boolean;
  };
}

interface Subscription {
  id: number;
  planId: number;
  status: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  plan: {
    name: string;
    price: number;
    features: string[];
  };
}

export default function ProfilePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { activeBusiness, setActiveBusinessId } = useBusinessContext();
  const [isEditing, setIsEditing] = useState(false);
  const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);
  const [isTwoFactorOpen, setIsTwoFactorOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [twoFactorData, setTwoFactorData] = useState({
    phoneNumber: "",
    code: "",
    step: "phone" as "phone" | "verify",
  });
  const [formData, setFormData] = useState<Partial<UserProfile>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch user profile data from /api/user (uses demo auth, includes profilePicture)
  const { data: profile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ["/api/user"],
  });

  // Fetch subscription data from billing API
  const { data: subscription } = useQuery<any>({
    queryKey: ["/api/billing/subscription"],
    queryFn: async () => {
      const res = await fetch("/api/billing/subscription", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
  });

  // Fetch businesses
  const { data: businesses = [] } = useQuery<any[]>({
    queryKey: ["/api/businesses"],
  });

  // Business Information shows + edits the user's CURRENTLY ACTIVE business,
  // not whatever happens to be at index 0 of the array. The previous version
  // wrote PATCHes to (businesses as any)?.[0] which silently retargeted the
  // wrong business when the active selection differed from array order.
  // Always re-resolve from the live array so the latest write is reflected.
  const targetBusinessId = (activeBusiness as any)?.id ?? (businesses as any)?.[0]?.id;
  const targetBusiness =
    (Array.isArray(businesses) && businesses.find((b: any) => b.id === targetBusinessId)) ||
    (businesses as any)?.[0] ||
    null;

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: async (data: Partial<UserProfile>) => {
      const res = await apiRequest("PATCH", "/api/auth/profile", data);
      return res.json();
    },
    onSuccess: (updatedUser) => {
      // Update cache directly — do NOT invalidate (the /api/auth/me endpoint uses Passport auth)
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      setIsEditing(false);
      toast({
        title: "Profile Updated",
        description: "Your profile has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error?.message || "There was an error updating your profile.",
        variant: "destructive",
      });
    },
  });

  // Update notification preferences
  const updateNotificationsMutation = useMutation({
    mutationFn: (data: any) => 
      apiRequest("PATCH", "/api/auth/notifications", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: "Preferences Updated",
        description: "Your notification preferences have been saved.",
      });
    },
  });

  // Upload profile picture
  const uploadProfilePictureMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('profilePicture', file);

      const response = await fetch('/api/auth/profile-picture', {
        method: 'POST',
        body: fd,
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to upload profile picture');
      return response.json();
    },
    onSuccess: (data) => {
      // Update cache directly with the new picture URL
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: "Profile Picture Updated",
        description: "Your profile picture has been updated successfully.",
      });
    },
    onError: () => {
      toast({
        title: "Upload Failed",
        description: "There was an error uploading your profile picture.",
        variant: "destructive",
      });
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: async (data: typeof passwordData) => {
      const response = await apiRequest("POST", "/api/auth/change-password", data);
      return response;
    },
    onSuccess: () => {
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setIsChangePasswordOpen(false);
      toast({
        title: "Password Changed",
        description: "Your password has been updated successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Password Change Failed",
        description: error.message || "Failed to change password. Please try again.",
        variant: "destructive",
      });
    },
  });

  // SMS Two-factor authentication setup mutation
  const setupTwoFactorMutation = useMutation({
    mutationFn: (phoneNumber: string) => 
      apiRequest("POST", "/api/auth/2fa/setup", { phoneNumber }),
    onSuccess: () => {
      setTwoFactorData({ ...twoFactorData, step: 'verify' });
      toast({
        title: "Verification Code Sent",
        description: "Please check your phone for the verification code.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "SMS Setup Failed",
        description: error.message || "Failed to send verification code.",
        variant: "destructive",
      });
    },
  });

  // SMS Two-factor authentication verification mutation
  const verifyTwoFactorMutation = useMutation({
    mutationFn: (data: { phoneNumber: string; code: string }) => 
      apiRequest("POST", "/api/auth/2fa/verify", data),
    onSuccess: (response: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: "Two-Factor Authentication Enabled",
        description: "Your account is now protected with SMS verification.",
      });
      if (response.backupCodes) {
        toast({
          title: "Backup Codes Generated",
          description: "Please save these backup codes in a safe place.",
        });
      }
      setIsTwoFactorOpen(false);
      setTwoFactorData({ phoneNumber: "", code: "", step: "phone" });
    },
    onError: (error: any) => {
      toast({
        title: "Verification Failed",
        description: error.message || "Invalid verification code.",
        variant: "destructive",
      });
    },
  });

  // Two-factor authentication toggle mutation
  const toggleTwoFactorMutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await apiRequest("POST", "/api/auth/two-factor", { enabled });
      return response.json() as Promise<{ enabled: boolean }>;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/user"] });
      toast({
        title: data.enabled ? "Two-Factor Enabled" : "Two-Factor Disabled",
        description: data.enabled 
          ? "Two-factor authentication has been enabled for your account."
          : "Two-factor authentication has been disabled for your account.",
      });
      setIsTwoFactorOpen(false);
      setTwoFactorData({ phoneNumber: "", code: "", step: "phone" });
    },
    onError: (error: any) => {
      toast({
        title: "Two-Factor Setup Failed",
        description: error.message || "Failed to update two-factor authentication.",
        variant: "destructive",
      });
    },
  });

  // Handler functions for SMS 2FA
  const handleTwoFactorSetup = () => {
    setupTwoFactorMutation.mutate(twoFactorData.phoneNumber);
  };

  const handleTwoFactorVerification = () => {
    verifyTwoFactorMutation.mutate({
      phoneNumber: twoFactorData.phoneNumber,
      code: twoFactorData.code,
    });
  };

  const handleSaveProfile = async () => {
    // /profile saves USER-only fields. Business fields have moved to
    // /business/profile (PATCH /api/business/profile from there).
    updateProfileMutation.mutate({
      fullName: (formData as any).fullName,
      email: (formData as any).email,
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          title: "Invalid File Type",
          description: "Please select an image file.",
          variant: "destructive",
        });
        return;
      }

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File Too Large",
          description: "Please select an image smaller than 5MB.",
          variant: "destructive",
        });
        return;
      }

      uploadProfilePictureMutation.mutate(file);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleNotificationChange = (key: string, value: boolean) => {
    const newNotifications = {
      ...profile?.emailNotifications,
      [key]: value
    };
    updateNotificationsMutation.mutate({ emailNotifications: newNotifications });
  };

  if (profileLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!profile) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Unable to load profile data.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Account Profile</h1>
            <p className="text-muted-foreground">
              Manage your account settings and preferences
            </p>
          </div>
          <Badge variant={subscription?.stripeSubscriptionId ? 'default' : 'secondary'}>
            {!subscription
              ? 'No Plan'
              : subscription.isTrial
                ? 'Free Trial'
                : (subscription.planName || 'No Plan')}
          </Badge>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Profile Information */}
          <div className="lg:col-span-2 space-y-6">
            {/* Personal Information */}
            <Card>
              <CardHeader className="flex flex-row items-center space-y-0 pb-2">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <CardTitle>Personal Information</CardTitle>
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-auto"
                  onClick={() => {
                    if (isEditing) {
                      handleSaveProfile();
                    } else {
                      setIsEditing(true);
                      // /profile is now USER-only. Business fields are managed
                      // on /business/profile.
                      setFormData({
                        fullName: profile.fullName,
                        email: profile.email,
                        username: profile.username,
                      } as any);
                    }
                  }}
                  disabled={updateProfileMutation.isPending}
                >
                  {isEditing ? "Save Changes" : "Edit"}
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Profile Picture Section */}
                <div className="flex items-center space-x-4 pb-4 border-b">
                  <div className="relative">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center text-white font-semibold text-xl overflow-hidden">
                      {profile?.profilePicture ? (
                        <img
                          src={profile.profilePicture}
                          alt="Profile"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        profile?.fullName?.charAt(0) || profile?.email?.charAt(0) || 'U'
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="absolute -bottom-2 -right-2 h-8 w-8 rounded-full p-0"
                      onClick={triggerFileSelect}
                      disabled={uploadProfilePictureMutation.isPending}
                    >
                      {uploadProfilePictureMutation.isPending ? (
                        <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
                      ) : (
                        <Camera className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  <div>
                    <h3 className="font-medium">{profile?.fullName || profile?.email}</h3>
                    <p className="text-sm text-muted-foreground">{profile?.email}</p>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-auto p-0 text-sm text-blue-600 hover:text-blue-800"
                      onClick={triggerFileSelect}
                      disabled={uploadProfilePictureMutation.isPending}
                    >
                      {uploadProfilePictureMutation.isPending ? 'Uploading...' : 'Change photo'}
                    </Button>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    {isEditing ? (
                      <Input
                        id="fullName"
                        value={formData.fullName || ""}
                        onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                      />
                    ) : (
                      <p className="text-sm">{profile.fullName || "Not provided"}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="username">Username</Label>
                    <p className="text-sm text-muted-foreground">{profile.email}</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address</Label>
                    {isEditing ? (
                      <Input
                        id="email"
                        type="email"
                        value={formData.email || ""}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      />
                    ) : (
                      <p className="text-sm">{profile.email}</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Member Since</Label>
                    <p className="text-sm flex items-center">
                      <Calendar className="h-4 w-4 mr-2" />
                      {format(new Date(profile.createdAt), "MMMM dd, yyyy")}
                    </p>
                  </div>
                </div>

                {/* Business details live on the dedicated Business Profile page */}
                <Separator className="my-4" />
                <div className="rounded-lg border bg-muted/30 p-4 flex items-start gap-3">
                  <Building className="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">Business details</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Business name, EIN, entity type, and address are managed per-business.
                      {targetBusiness?.name && (
                        <>
                          {" "}Currently active: <span className="font-medium">{targetBusiness.name?.trim()}</span>.
                        </>
                      )}
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      if (targetBusiness?.id) setActiveBusinessId(targetBusiness.id);
                      navigate("/business/profile");
                    }}
                  >
                    Open Business Profile →
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Notification Preferences */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Bell className="h-5 w-5" />
                  <CardTitle>Notification Preferences</CardTitle>
                </div>
                <CardDescription>
                  Choose what notifications you'd like to receive
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="reports-summary">Reports Summary</Label>
                      <p className="text-sm text-muted-foreground">
                        Receive weekly financial report summaries
                      </p>
                    </div>
                    <Switch
                      id="reports-summary"
                      checked={profile?.emailNotifications?.reportsSummary ?? true}
                      onCheckedChange={(checked) => 
                        handleNotificationChange('reportsSummary', checked)
                      }
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="task-reminders">Task Reminders</Label>
                      <p className="text-sm text-muted-foreground">
                        Get reminded about upcoming deadlines and tasks
                      </p>
                    </div>
                    <Switch
                      id="task-reminders"
                      checked={profile?.emailNotifications?.taskReminders ?? true}
                      onCheckedChange={(checked) => 
                        handleNotificationChange('taskReminders', checked)
                      }
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="subscription-alerts">Subscription Alerts</Label>
                      <p className="text-sm text-muted-foreground">
                        Important updates about your subscription
                      </p>
                    </div>
                    <Switch
                      id="subscription-alerts"
                      checked={profile?.emailNotifications?.subscriptionAlerts ?? true}
                      onCheckedChange={(checked) => 
                        handleNotificationChange('subscriptionAlerts', checked)
                      }
                    />
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="marketing-emails">Marketing Emails</Label>
                      <p className="text-sm text-muted-foreground">
                        Product updates and promotional content
                      </p>
                    </div>
                    <Switch
                      id="marketing-emails"
                      checked={profile?.emailNotifications?.marketingEmails ?? false}
                      onCheckedChange={(checked) => 
                        handleNotificationChange('marketingEmails', checked)
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Subscription Info */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5" />
                  <CardTitle>Subscription</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Plan</span>
                    <Badge variant={subscription?.stripeSubscriptionId ? 'default' : 'secondary'}>
                      {!subscription
                        ? 'No Plan'
                        : subscription.isTrial
                          ? 'Free Trial'
                          : (subscription.planName || 'No Plan')}
                    </Badge>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Status</span>
                    <div className="flex items-center space-x-1">
                      {(() => {
                        // Status is derived from sub.status when present.
                        // Don't gate on stripeSubscriptionId — the backfilled
                        // per-business row may have status='active' without
                        // its own Stripe ID (the tenant Stripe sub lives on
                        // a different row).
                        const status = subscription?.status?.toLowerCase();
                        let label: string;
                        let healthy: boolean;
                        if (!subscription) {
                          label = "No Plan"; healthy = false;
                        } else if (subscription.isTrial) {
                          label = "Trial"; healthy = true;
                        } else if (status === "active") {
                          label = "Active"; healthy = true;
                        } else if (status === "cancelling") {
                          label = "Cancelling"; healthy = false;
                        } else if (status === "past_due") {
                          label = "Past due"; healthy = false;
                        } else if (status === "cancelled" || status === "canceled") {
                          label = "Cancelled"; healthy = false;
                        } else {
                          label = subscription.status || "Inactive"; healthy = false;
                        }
                        return (
                          <>
                            {healthy ? (
                              <CheckCircle className="h-4 w-4 text-green-500" />
                            ) : (
                              <AlertCircle className="h-4 w-4 text-yellow-500" />
                            )}
                            <span className="text-sm capitalize">{label}</span>
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  {subscription?.currentPeriodEnd && (
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Next Billing</span>
                      <span className="text-sm">
                        {format(new Date(subscription.currentPeriodEnd), "MMM dd, yyyy")}
                      </span>
                    </div>
                  )}
                </div>
                
                <Separator />
                
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => window.location.href = "/subscription"}
                >
                  Manage Subscription
                </Button>
              </CardContent>
            </Card>

            {/* Businesses */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Building className="h-5 w-5" />
                  <CardTitle>Your Businesses</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {Array.isArray(businesses) && businesses.map((business: any) => {
                  const sub = business.subscription;
                  const planLabel = !sub
                    ? "No Plan"
                    : sub.isTrial || sub.is_trial
                      ? "Free Trial"
                      : (sub.planName || sub.plan_name || "No Plan");
                  const staff = Array.isArray(business.assignedStaff) ? business.assignedStaff : [];
                  const staffLabel = staff.length === 0
                    ? "No staff assigned"
                    : staff.map((s: any) => s.name).join(", ");
                  return (
                    <button
                      key={business.id}
                      type="button"
                      onClick={() => {
                        setActiveBusinessId(business.id);
                        navigate("/business/profile");
                      }}
                      className="group w-full text-left flex items-start space-x-2 p-2 border rounded hover:bg-accent hover:text-accent-foreground hover:border-accent-foreground/20 hover:shadow-sm transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/40"
                      title={`Open profile for ${business.name?.trim() || "this business"}`}
                    >
                      <Building className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0 group-hover:text-accent-foreground" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate group-hover:text-accent-foreground">{business.name?.trim() || "Unnamed Business"}</p>
                        <p className="text-xs text-muted-foreground capitalize group-hover:text-accent-foreground/80">{business.industry || "General"}</p>
                        <p className="text-[11px] text-muted-foreground mt-1 group-hover:text-accent-foreground/80">
                          <span className="font-medium">Plan:</span> {planLabel}
                        </p>
                        <p className="text-[11px] text-muted-foreground group-hover:text-accent-foreground/80">
                          <span className="font-medium">Staff:</span>{" "}
                          <span className={staff.length === 0 ? "italic" : ""}>{staffLabel}</span>
                        </p>
                      </div>
                    </button>
                  );
                })}
                
                {(!businesses || businesses.length === 0) && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No businesses added yet
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Account Security */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Shield className="h-5 w-5" />
                  <CardTitle>Security</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Change Password Dialog */}
                <Dialog open={isChangePasswordOpen} onOpenChange={setIsChangePasswordOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Settings className="h-4 w-4 mr-2" />
                      Change Password
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Change Password</DialogTitle>
                      <DialogDescription>
                        Enter your current password and choose a new one to update your account security.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="currentPassword">Current Password</Label>
                        <Input
                          id="currentPassword"
                          type="password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                          placeholder="Enter current password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="newPassword">New Password</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={passwordData.newPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                          placeholder="Enter new password"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="confirmPassword">Confirm New Password</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                          placeholder="Confirm new password"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsChangePasswordOpen(false)}
                        disabled={changePasswordMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => {
                          if (passwordData.newPassword !== passwordData.confirmPassword) {
                            toast({
                              title: "Password Mismatch",
                              description: "New passwords do not match. Please try again.",
                              variant: "destructive",
                            });
                            return;
                          }
                          if (passwordData.newPassword.length < 8) {
                            toast({
                              title: "Password Too Short",
                              description: "Password must be at least 8 characters long.",
                              variant: "destructive",
                            });
                            return;
                          }
                          changePasswordMutation.mutate(passwordData);
                        }}
                        disabled={changePasswordMutation.isPending || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                      >
                        {changePasswordMutation.isPending ? "Changing..." : "Change Password"}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                
                {/* Two-Factor Authentication Dialog */}
                <Dialog open={isTwoFactorOpen} onOpenChange={setIsTwoFactorOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full justify-start">
                      <Shield className="h-4 w-4 mr-2" />
                      Two-Factor Authentication
                      {profile?.twoFactorEnabled && (
                        <Badge variant="secondary" className="ml-auto">Enabled</Badge>
                      )}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Two-Factor Authentication</DialogTitle>
                      <DialogDescription>
                        {profile?.twoFactorEnabled 
                          ? "Two-factor authentication is currently enabled for your account. You can disable it below."
                          : "Add an extra layer of security to your account by enabling SMS-based two-factor authentication."
                        }
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {!profile?.twoFactorEnabled && (
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="phoneNumber">Phone Number</Label>
                            <Input
                              id="phoneNumber"
                              type="tel"
                              placeholder="+1234567890"
                              value={twoFactorData.phoneNumber}
                              onChange={(e) => setTwoFactorData({ ...twoFactorData, phoneNumber: e.target.value })}
                            />
                            <p className="text-xs text-muted-foreground">
                              We'll send a verification code to this number
                            </p>
                          </div>
                          {twoFactorData.step === 'verify' && (
                            <div className="space-y-2">
                              <Label htmlFor="verificationCode">Verification Code</Label>
                              <Input
                                id="verificationCode"
                                type="text"
                                placeholder="Enter 6-digit code"
                                value={twoFactorData.code}
                                onChange={(e) => setTwoFactorData({ ...twoFactorData, code: e.target.value })}
                              />
                              <p className="text-xs text-muted-foreground">
                                Check your phone for the verification code
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center space-x-2">
                        <Shield className="h-8 w-8 text-green-600" />
                        <div>
                          <p className="font-medium">
                            {profile?.twoFactorEnabled ? "Two-Factor Authentication is Enabled" : "Enable SMS Two-Factor Authentication"}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {profile?.twoFactorEnabled 
                              ? "Your account is protected with SMS verification."
                              : "Receive verification codes via text message for enhanced security."
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button 
                        variant="outline" 
                        onClick={() => setIsTwoFactorOpen(false)}
                        disabled={toggleTwoFactorMutation.isPending}
                      >
                        Cancel
                      </Button>
                      <Button 
                        onClick={() => {
                          if (profile?.twoFactorEnabled) {
                            toggleTwoFactorMutation.mutate(false);
                          } else if (twoFactorData.step === 'phone') {
                            if (!twoFactorData.phoneNumber) {
                              toast({
                                title: "Phone Number Required",
                                description: "Please enter your phone number to continue.",
                                variant: "destructive",
                              });
                              return;
                            }
                            handleTwoFactorSetup();
                          } else if (twoFactorData.step === 'verify') {
                            handleTwoFactorVerification();
                          }
                        }}
                        disabled={toggleTwoFactorMutation.isPending || setupTwoFactorMutation.isPending || verifyTwoFactorMutation.isPending}
                        variant={profile?.twoFactorEnabled ? "destructive" : "default"}
                      >
                        {(toggleTwoFactorMutation.isPending || setupTwoFactorMutation.isPending || verifyTwoFactorMutation.isPending)
                          ? "Processing..." 
                          : profile?.twoFactorEnabled 
                            ? "Disable Two-Factor" 
                            : twoFactorData.step === 'phone'
                              ? "Send Verification Code"
                              : "Verify & Enable"
                        }
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
